import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  getRequestFoundCandidatesCount,
  RequestItem,
  useAdministrationRequestApproveAPI,
  useCurrentRequestById,
  useDefaultRequestById,
  useRequestsAPI,
  useResizeObserver,
  useValidationSchemaRequest,
} from '@innowise-group/core';
import { requestFormDefaultValues } from '@innowise-group/modals';
import { Loader, Modal, modalHoc, ModalProps, notificationsManager, useModal, useTheme } from '@innowise-group/mui-kit';
import { RequestForm } from '@shared-components';

import { RequestNonBillableWarningModal } from '../request-non-billable-warning-modal';
import * as Styled from './request-edition-modal.styles';

interface RequestEditionModalProps {
  requestId: number;
  successEditionCallback?: () => void;
}

const RequestEditionModal: React.FC<ModalProps & RequestEditionModalProps> = ({
  close,
  requestId,
  successEditionCallback,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktopView = useResizeObserver(theme.breakpoints.values.sm);

  const requestDefault = useDefaultRequestById();
  const initialRequestEntity = useCurrentRequestById();

  const { isLoading: requestsIsLoading, updateRequest } = useRequestsAPI();
  const { isLoading: requestApproversIsLoading } = useAdministrationRequestApproveAPI();

  const { validationSchema } = useValidationSchemaRequest(
    requestDefault?.deadline,
    initialRequestEntity ? Math.max(getRequestFoundCandidatesCount(initialRequestEntity), 1) : undefined,
  );
  const methods = useForm<RequestItem>({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: requestFormDefaultValues,
  });

  const { handleSubmit, reset, watch } = methods;
  const isBillable = watch('isBillable');

  const [showRequestNonBillableWarningModal] = useModal(RequestNonBillableWarningModal);

  const isNonBillableWarningToBeShown = useMemo(() => {
    if (isBillable === 'false' && initialRequestEntity.isBillable) {
      return true;
    }

    return false;
  }, [isBillable, initialRequestEntity?.isBillable]);

  // US-10.3 AC-8: a failed save shows its own message instead of the backend one, the form keeps the entered values
  const onSubmit = async (data: RequestItem) => {
    const result = updateRequest(data, requestId, successCallback, { ignoreGlobalCatch: true });

    if (isNonBillableWarningToBeShown) {
      showRequestNonBillableWarningModal();
    }

    if ((await result).meta.requestStatus === 'rejected') {
      notificationsManager.error({ subtitle: t('notifications.text.requestChangesNotSaved') });
    }
  };

  const successCallback = () => {
    notificationsManager.success({ subtitle: t('notifications.text.requestChangesSaved') });
    close();
    successEditionCallback?.();
  };

  // TODO(ITSRT2-1395): GET /claim/process (the non-billable approvers) is removed on the backend and comes back under
  //  a new path, maybe in a new format. The call is off until then not to fail with 404
  // useEffect(() => {
  //   getAdministrationRequestApprove();
  // }, [getAdministrationRequestApprove]);

  useEffect(() => {
    if (requestDefault) {
      reset(requestDefault);
    }
  }, [initialRequestEntity?.id]);

  const selectedCustomersOptions = useMemo(() => {
    if (!requestDefault) return [];
    return requestDefault.customers.map((item) => item.value);
  }, [initialRequestEntity?.id]);

  return (
    <Modal.Container size="large">
      {requestsIsLoading || requestApproversIsLoading ? (
        <Loader />
      ) : (
        <FormProvider {...methods}>
          <Modal.Header onClose={close}>
            <Styled.Header>
              <span>{t('pages.requests.creationModal.requestEdition')}</span>
            </Styled.Header>
          </Modal.Header>
          <Styled.Body>
            <RequestForm
              isEdit
              onSubmit={onSubmit}
              handleClose={close}
              selectedCustomersOptions={selectedCustomersOptions}
              isNonBillableWarningToBeShown={isNonBillableWarningToBeShown}
            />
          </Styled.Body>
          {isDesktopView && (
            <Styled.Footer>
              <Styled.CancelButton onClick={close} variant="outlined">
                {t('buttons.cancel')}
              </Styled.CancelButton>
              <Styled.SubmitButton onClick={handleSubmit(onSubmit)}>{t('buttons.save')}</Styled.SubmitButton>
            </Styled.Footer>
          )}
        </FormProvider>
      )}
    </Modal.Container>
  );
};

export default modalHoc(RequestEditionModal, 'request-edition');
